(function () {
	'use strict';
	// Login sync with the Hydrogen storefront (nativepath.com) via shared
	// `.nativepath.com` cookies — Shopify's own session cookies are host-only.
	// Same technique as cart-sync.js. Full mechanism: docs/login-sync.md
	//
	//   theme -> Hydrogen   `np_customer`            signed-in | signed-out
	//   Hydrogen -> theme   `np_hydrogen_customer`   signed-in only
	if (!/(^|\.)nativepath\.com$/.test(location.hostname)) return;

	// Matches Shopify's documented customer sign-in session — "up to 365 days"
	// (help.shopify.com/en/manual/customers/customer-accounts), which is also the
	// Max-Age Shopify sets on its own `_shopify_essential` container (31536000).
	// A shorter value would expire the flag while the session is still live, and
	// Hydrogen would stop syncing a customer who is genuinely signed in.
	var SESSION_MAX_AGE = 60 * 60 * 24 * 365;
	var SIGNED_IN = 'signed-in';
	var SIGNED_OUT = 'signed-out';

	function setShared(name, value, maxAge) {
		document.cookie =
			name +
			'=' +
			value +
			'; Domain=.nativepath.com; Path=/; SameSite=Lax; Secure; Max-Age=' +
			maxAge;
	}

	function themeState() {
		var match = /(?:^|;\s*)np_customer=([\w-]+)(?:\s*;|$)/.exec(document.cookie);
		return match ? match[1] : null;
	}

	function hydrogenHasSession() {
		return new RegExp('(?:^|;\\s*)np_hydrogen_customer=' + SIGNED_IN + '(?:\\s*;|$)').test(
			document.cookie,
		);
	}

	// Recharge's portal is app-proxied onto /tools/recurring/* and authenticates on
	// its own token, so a shopper arriving from its email has no online-store session
	// and looks claimable. Claiming would navigate away from a token-bearing URL and
	// hand back a session the portal does not accept. Mirrors Hydrogen's `/account/*`
	// skip. See docs/login-sync.md.
	function isAppProxy() {
		return /^\/(tools|apps)(\/|$)/.test(location.pathname);
	}

	// One-shot per tab; false when storage is unavailable, so private mode skips
	// the sync rather than risking a redirect loop.
	function claimOnce(key) {
		try {
			if (sessionStorage.getItem(key)) return false;
			sessionStorage.setItem(key, '1');
			return true;
		} catch (error) {
			return false;
		}
	}

	// Releases the one-shot above. Only ever called once the theme is signed in:
	// the guard exists to stop a *failed* claim retrying, so a successful one must
	// hand it back or this tab can never claim again after a later sign-out.
	function releaseClaim(key) {
		try {
			sessionStorage.removeItem(key);
		} catch (error) {
			// Storage unavailable; the claim was skipped anyway.
		}
	}

	function claimHydrogenSession() {
		location.replace(
			'/customer_authentication/login?return_to=' +
				encodeURIComponent(location.pathname + location.search),
		);
	}

	// Follow whatever happened on Hydrogen while this tab sat idle: a sign-out expires
	// `np_customer` (reload), a sign-in sets `np_hydrogen_customer` (claim it).
	// Tab events as in cart-sync.js.
	//
	//   state.renderedSignedIn  this page rendered with a customer
	function watchHydrogen(state) {
		if (typeof document.addEventListener !== 'function') return;

		var THROTTLE_MS = 5000;
		var lastCheck = Date.now();

		function onReturn() {
			try {
				if (document.visibilityState !== 'visible') return;
				var now = Date.now();
				if (now - lastCheck < THROTTLE_MS) return;
				lastCheck = now;
				if (state.renderedSignedIn) {
					// Only a full render is authoritative: `<shopify-account>` gets its
					// signed-in state from a shadow root Shopify injects per page, which
					// `?sections=header` does not carry. See docs/login-sync.md.
					// Not on app-proxy pages — a reload there would discard an
					// in-progress subscription edit to fix a stale avatar. The next
					// navigation renders the right header anyway.
					//
					// One-shot, like the claim: `np_customer` goes missing both when
					// Hydrogen signs out (reload fixes the header) and when its silent
					// probe fails while this session is still alive (reload fixes
					// nothing and the theme rewrites the flag). Without the guard the
					// second case reloads the page on every tab return, losing scroll
					// position and any half-filled form.
					if (
						!isAppProxy() &&
						themeState() !== SIGNED_IN &&
						claimOnce('np_reload_tried')
					) {
						location.reload();
					}
				} else if (
					!isAppProxy() &&
					!themeState() &&
					hydrogenHasSession() &&
					claimOnce('np_sso_tried')
				) {
					claimHydrogenSession();
				}
			} catch (error) {
				// Never throw from a focus handler.
			}
		}

		document.addEventListener('visibilitychange', onReturn);
		if (typeof window !== 'undefined' && window.addEventListener) {
			window.addEventListener('pageshow', function (event) {
				if (event && event.persisted) onReturn();
			});
		}
	}

	if (document.currentScript.dataset.loggedIn === 'true') {
		// Clear Hydrogen's bounce guard on a genuine sign-in, so a customer who has
		// just signed in syncs now instead of waiting out the guard. Two ways to know
		// it is genuine: an observed signed-out -> signed-in transition, or the first
		// signed-in render in this tab — which is where a sign-in lands when Hydrogen
		// has already deleted `np_customer` after disproving an earlier one.
		//
		// Once per tab, not per render. Every render would re-arm the bounce on every
		// crossover, which is the loop the guard exists to stop.
		if (themeState() === SIGNED_OUT || (!themeState() && claimOnce('np_signin_seen'))) {
			setShared('np_sso_tried', '', 0);
		}
		// Same for this tab's one-shot: sign out on Hydrogen and back in, and the
		// second claim must still be allowed. sessionStorage survives reloads, so
		// without this the tab is stuck signed-out until it is closed.
		releaseClaim('np_sso_tried');
		setShared('np_customer', SIGNED_IN, SESSION_MAX_AGE);
		watchHydrogen({ renderedSignedIn: true });
		return;
	}

	// Hydrogen has a session we don't — claim it. Only when we have no opinion at all:
	// any `np_customer` we published (`signed-in` or `signed-out`) means we know the
	// state here, and claiming on `signed-out` would dump someone who just signed out
	// on a login screen. A genuine Hydrogen login deletes a stale `signed-out`
	// (clearStaleSignedOut), so the legitimate case is always an absent cookie.
	if (!isAppProxy() && !themeState() && hydrogenHasSession() && claimOnce('np_sso_tried')) {
		claimHydrogenSession();
		return;
	}

	// Only report a sign-out we actually observed — a transition out of `signed-in`.
	// Not merely because Hydrogen has a session and our claim was skipped (already
	// used this tab, or sessionStorage unavailable): that says nothing about whether
	// anyone signed out, and Hydrogen would destroy a live session. A visitor who has
	// never signed in gets no cookie from this file at all.
	if (themeState() === SIGNED_IN) {
		setShared('np_customer', SIGNED_OUT, SESSION_MAX_AGE);
	}
	// This page rendered signed-out, so any reload above achieved what it was for.
	// Held otherwise: a reload that lands on another signed-in render fixed nothing
	// and must not repeat.
	releaseClaim('np_reload_tried');
	watchHydrogen({ renderedSignedIn: false });
})();
